import React, { useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query/react';

import dayjs, { dateTimeDisplayFormat } from '~libs/dayjs';

import { ErrorResponse, Spinner } from '~components';

import { Detail, SupplierInvoiceActivityItemChange } from '~support-at-home/models';

import { useGetInvoiceLogsQuery } from '~support-at-home/actions/supplierInvoices';

interface ComponentProps {
  id?: string;
}

const SupplierInvoiceActivityLog: React.FC<ComponentProps> = ({ id }) => {
  const { data: logs = [], isLoading, isError } = useGetInvoiceLogsQuery(id ? { id } : skipToken);

  const buildChange = (change: Detail, key: string) => {
    const { field, from, to } = change;
    const changeText = from ? `"${from}" to "${to}"` : `Set to "${to}"`;

    return (
      <Row key={key}>
        <Col xs={3} className="text-end pe-2">
          {field}:
        </Col>
        <Col>
          <span>{changeText}</span>
        </Col>
      </Row>
    );
  };

  const builtLogs = useMemo(() => {
    return logs.map(({ created_at, external_reference_user_name, changes }) => {
      const { invoice, items } = changes;
      const allChanges = [...invoice, ...items.flatMap((item: SupplierInvoiceActivityItemChange) => item.changes)];

      if (allChanges.length === 0) return null;

      return (
        <Row key={created_at} className="mb-3 w-100 border-bottom pb-2">
          <Col sm={3}>
            <strong>{dayjs(created_at).format(dateTimeDisplayFormat)}</strong>
            <div className="text-muted small">{external_reference_user_name ?? 'System'}</div>
          </Col>
          <Col sm={9}>{allChanges.map((change, idx) => buildChange(change, `${created_at}-${idx}`))}</Col>
        </Row>
      );
    });
  }, [logs]);

  if (isLoading) {
    return <Spinner loading={true} />;
  }

  if (isError) {
    return <ErrorResponse message="Unable to load activity log." />;
  }

  if (logs.length === 0) {
    return <div className="text-muted p-3">No activity recorded for this invoice.</div>;
  }

  return <div className="supplier-invoice-activity-log p-2">{builtLogs}</div>;
};

export default SupplierInvoiceActivityLog;
